import { ReactNode, useLayoutEffect, useRef } from 'react';
import { Location } from 'react-router-dom';
import { RouteTransition } from '../../../utils/routeConfig';
import { TransitionDirection } from './transitions';
import './PageTransitionStage.css';

/** Animation duration in ms. Kept in sync with PageTransitionStage.css. */
export const PAGE_TRANSITION_MS = 320;

interface PageTransitionStageProps {
  location: Location;
  transition: RouteTransition;
  direction: TransitionDirection;
  children: (location: Location) => ReactNode;
}

/**
 * Single-slot routing stage with path-keyed enter animation.
 *
 * The inner wrapper is keyed by `location.pathname` rather than
 * `location.key`. This matters because contexts like AssetsContext and
 * OverviewContext canonicalize their state into the URL via
 * `setSearchParams(..., { replace: true })` on interaction. Every such
 * replace mints a new `location.key`, so keying on it would re-mount the
 * route subtree and replay the enter animation on every tab click —
 * which historically caused the "continuous flashing" you saw.
 *
 * The enter animation is applied by directly toggling the `page--enter`
 * class on the newly-mounted div from a `useLayoutEffect`, rather than
 * baking it into the initial className. That gives us two things:
 *   1. The class is added BEFORE the first paint of the new div, so the
 *      animation starts at frame 0 with no one-frame jank where the content
 *      briefly sits at its final (post-animation) transform.
 *   2. We can skip the animation on the very first app-load render (no
 *      previous pathname → nothing to animate from).
 *
 * Outgoing animation: intentionally none. Rendering the previous route's
 * React tree in parallel with the new one duplicated page-level contexts
 * (each trying to write the URL), which was the root cause of the
 * replaceState throttle crash. Single-slot rendering guarantees correctness
 * — the incoming animation alone still communicates direction and motion.
 */
const PageTransitionStage = ({
  location,
  transition,
  direction,
  children,
}: PageTransitionStageProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const prevPathRef = useRef<string>(location.pathname);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    // First mount, or same pathname (e.g. query-param change) — no animation.
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    const el = contentRef.current;
    if (!el) return;

    // Restart animation cleanly if a previous one was in-flight.
    el.classList.remove('page--enter');
    // Force a reflow so the browser acknowledges the class removal before
    // we add it back — otherwise adding it again on the same frame is a
    // no-op and the animation won't restart.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    el.offsetWidth;
    el.classList.add('page--enter');

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Strip the class after the animation completes so the compositor
      // layer hint (will-change) is released.
      if (contentRef.current) {
        contentRef.current.classList.remove('page--enter');
      }
      timerRef.current = null;
    }, PAGE_TRANSITION_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [location.pathname]);

  return (
    <div
      className="page-stage"
      data-transition={transition}
      data-direction={direction}
    >
      {/*
       * key={location.pathname} remounts only on real path changes; search
       * params don't trigger a remount, which is important for pages that
       * self-sync to the URL.
       */}
      <div ref={contentRef} key={location.pathname} className="page">
        {children(location)}
      </div>
    </div>
  );
};

export default PageTransitionStage;
